<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md shadow-sm hover:shadow-md transition-shadow"
  >
    <div
      class="trezo-card-header mb-[20px] md:mb-[25px] flex items-center justify-between"
    >
      <div class="trezo-card-title">
        <h5 class="mb-0 text-lg font-semibold">{{ directT('dashboard.crm.recentLeads') }}</h5>
      </div>
      
      <div class="trezo-card-subtitle">
        <div class="trezo-card-dropdown relative">
          <button
            type="button"
            :class="{
              'trezo-card-dropdown-btn inline-block transition-all hover:text-primary-500 focus:outline-none': true,
              active: isActive,
            }"
            id="dropdownToggle"
            @click.stop="toggleActive"
          >
            <span
              class="inline-block relative ltr:pr-[17px] ltr:md:pr-[20px] rtl:pl-[17px] rtl:ml:pr-[20px] font-medium"
            >
              {{ selectedTimeFilter }}
              <i
                class="ri-arrow-down-s-line text-lg absolute ltr:-right-[3px] rtl:-left-[3px] top-1/2 -translate-y-1/2"
              ></i>
            </span>
          </button>

          <ul
            class="trezo-card-dropdown-menu transition-all bg-white shadow-lg rounded-md top-full py-[15px] absolute ltr:right-0 rtl:left-0 w-[195px] z-[5] dark:bg-[#0c1427] dark:shadow-md"
            v-if="isActive"
          >
            <li v-for="filter in timeFilters" :key="filter">
              <button
                type="button"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-[#172036]"
                @click="applyTimeFilter(filter)"
              >
                {{ filter }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="trezo-card-content" style="min-height: 420px;">
      <!-- Loading state with enhanced spinner -->
      <div v-if="loading" class="flex justify-center items-center h-[420px]">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 opacity-75"></div>
      </div>
      
      <!-- Enhanced error state -->
      <div v-else-if="error" class="flex flex-col justify-center items-center h-[420px] text-center px-6">
        <div class="text-danger-500 text-3xl mb-3">
          <i class="ri-error-warning-line"></i>
        </div>
        <div class="text-danger-500 font-medium mb-2">{{ directT('dashboard.crm.unableToLoadLeads') }}</div>
        <div class="text-gray-500 dark:text-gray-400 text-sm">{{ error }}</div>
      </div>
      
      <!-- Enhanced empty state -->
      <div v-else-if="leads.length === 0" class="flex flex-col justify-center items-center h-[420px] text-center px-6">
        <div class="text-gray-400 text-3xl mb-3">
          <i class="ri-file-list-3-line"></i>
        </div>
        <div class="text-gray-700 dark:text-gray-300 font-medium mb-2">{{ directT('dashboard.crm.noLeadsFound') }}</div>
        <div class="text-gray-500 dark:text-gray-400 text-sm">{{ directT('dashboard.crm.tryChangingFilter') }}</div>
      </div>
      
      <div v-else class="-mx-[20px] md:-mx-[25px]">
        <div class="table-responsive overflow-x-auto">
          <table class="w-full">
            <thead class="text-black dark:text-white">
              <tr>
                <th
                  class="font-medium ltr:text-left rtl:text-right px-[20px] py-[12px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tl-md last:rounded-tr-md"
                >
                  {{ directT('dashboard.customer') }}
                </th>
                <th
                  class="font-medium ltr:text-left rtl:text-right px-[20px] py-[12px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
                >
                  {{ directT('dashboard.crm.email') }}
                </th>
                <th
                  class="font-medium ltr:text-left rtl:text-right px-[20px] py-[12px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
                >
                  {{ directT('dashboard.crm.source') }}
                </th>
                <th
                  class="font-medium ltr:text-left rtl:text-right px-[20px] py-[12px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
                >
                  {{ directT('dashboard.status') }}
                </th>
                <th
                  class="font-medium ltr:text-left rtl:text-right px-[20px] py-[12px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 bg-primary-50 dark:bg-[#15203c] whitespace-nowrap"
                >
                  {{ directT('dashboard.crm.action') }}
                </th>
              </tr>
            </thead>
            <tbody class="text-black dark:text-white">
              <tr v-for="item in paginatedItems" :key="item.id" class="hover:bg-gray-50 dark:hover:bg-[#172036] transition-colors">
                <td
                  class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
                >
                  <div class="flex items-center">
                    <div class="rounded-full w-[40px] h-[40px] overflow-hidden border border-gray-100 dark:border-[#172036] shadow-sm">
                      <img
                        :src="getLeadAvatar(item)"
                        class="w-full h-full object-cover"
                        alt="user-image"
                      />
                    </div>
                    <div class="ltr:ml-[12px] rtl:mr-[12px]">
                      <span class="block font-medium">
                        {{ item.name }}
                      </span>
                    </div>
                  </div>
                </td>
                <td
                  class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
                >
                  {{ item.email }}
                </td>
                <td
                  class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
                >
                  {{ item.source }}
                </td>
                <td
                  class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
                >
                  <span
                    class="px-[10px] py-[4px] inline-block rounded-md font-medium text-xs"
                    :style="getStatusClass(item.status)"
                  >
                    {{ item.status }}
                  </span>
                </td>
                <td
                  class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] md:ltr:first:pl-[25px] md:rtl:first:pr-[25px] ltr:first:pr-0 rtl:first:pl-0 border-b border-gray-100 dark:border-[#172036]"
                >
                  <div class="flex items-center gap-[12px]">
                    <button
                      type="button"
                      class="text-gray-500 dark:text-gray-400 leading-none custom-tooltip hover:text-primary-500 transition-colors"
                      id="customTooltip"
                      data-text="Edit"
                      @click="editLead(item)"
                    >
                      <i class="material-symbols-outlined !text-md">
                        edit
                      </i>
                    </button>
                    <button
                      type="button"
                      class="text-gray-500 dark:text-gray-400 leading-none custom-tooltip hover:text-danger-500 transition-colors"
                      id="customTooltip"
                      data-text="Delete"
                      @click="deleteLead(item)"
                    >
                      <i class="material-symbols-outlined !text-md">
                        delete
                      </i>
                    </button>
                  </div>
                </td>
              </tr>
              <!-- Placeholders to maintain fixed height if fewer than 5 rows -->
              <template v-if="paginatedItems.length < 5">
                <tr v-for="i in (5 - paginatedItems.length)" :key="`placeholder-${i}`">
                  <td colspan="5" class="border-b border-gray-100 dark:border-[#172036] h-[65px]"></td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Enhanced pagination -->
        <div
          class="px-[20px] md:px-[25px] pt-[12px] md:pt-[14px] sm:flex sm:items-center justify-between"
        >
          <p class="mb-0 text-sm text-gray-600 dark:text-gray-400">
            {{ directT('dashboard.showingResults').replace('{start}', String(startItem + 1)).replace('{end}', String(endItem)).replace('{total}', String(leads.length)) }}
          </p>
          <ol class="mt-[10px] sm:mt-0 flex">
            <li
              class="mx-[3px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
              :class="{ disabled: currentPage === 1 }"
            >
              <a
                href="#"
                class="w-[32px] h-[32px] flex items-center justify-center relative text-center rounded-md border border-gray-200 dark:border-[#172036] transition-all hover:bg-primary-500 hover:text-white hover:border-primary-500 disabled:opacity-50"
                @click.prevent="prevPage"
              >
                <i class="material-symbols-outlined !text-[18px]">
                  chevron_left
                </i>
              </a>
            </li>
            <li
              class="mx-[3px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
              v-for="page in totalPages"
              :key="page"
              :class="{ active: currentPage === page }"
            >
              <a
                href="#"
                class="w-[32px] h-[32px] flex items-center justify-center relative text-center rounded-md border border-gray-200 dark:border-[#172036] transition-all hover:bg-primary-500 hover:text-white hover:border-primary-500"
                @click.prevent="goToPage(page)"
              >
                {{ page }}
              </a>
            </li>
            <li
              class="mx-[3px] ltr:first:ml-0 ltr:last:mr-0 rtl:first:mr-0 rtl:last:ml-0"
              :class="{ disabled: currentPage === totalPages }"
            >
              <a
                href="#"
                class="w-[32px] h-[32px] flex items-center justify-center relative text-center rounded-md border border-gray-200 dark:border-[#172036] transition-all hover:bg-primary-500 hover:text-white hover:border-primary-500 disabled:opacity-50"
                @click.prevent="nextPage"
              >
                <i class="material-symbols-outlined !text-[18px]">
                  chevron_right
                </i>
              </a>
            </li>
          </ol>
        </div>
      </div>
    </div>
  </div>

  <!-- Enhanced Delete Confirmation Modal -->
  <div v-if="showDeleteModal" class="fixed inset-0 flex items-center justify-center z-50">
    <div class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" @click="closeDeleteModal"></div>
    <div class="bg-white dark:bg-[#0c1427] p-5 sm:p-6 rounded-lg shadow-xl w-full max-w-md mx-2 sm:mx-4 z-10 relative transform transition-all">
      <div class="flex justify-between items-center mb-5">
        <h3 class="text-lg font-semibold text-black dark:text-white">{{ directT('dashboard.crm.confirmDeletion') }}</h3>
        <button
          @click="closeDeleteModal" 
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 -m-2 rounded-full hover:bg-gray-100 dark:hover:bg-[#172036] transition-colors"
        >
          <i class="material-symbols-outlined text-xl">close</i>
        </button>
      </div>
      
      <div class="mb-6">
        <div class="text-danger-500 text-center mb-4">
          <i class="material-symbols-outlined text-3xl">warning</i>
        </div>
        <p class="text-gray-700 dark:text-gray-300 text-sm sm:text-base">
          {{ directT('dashboard.crm.deleteConfirmation') }}
        </p>
      </div>
      
      <div class="flex justify-between items-center pt-3">
        <button
          type="button"
          @click="closeDeleteModal"
          class="inline-block py-[10px] px-[20px] bg-gray-200 text-gray-700 dark:bg-[#172036] dark:text-gray-300 transition-all hover:bg-gray-300 dark:hover:bg-[#15203c] rounded-md"
        >
          <span class="flex items-center">
            <i class="material-symbols-outlined mr-2">close</i>
            {{ directT('dashboard.crm.cancel') }}
          </span>
        </button>
        <button
          type="button"
          @click="confirmDelete"
          class="inline-block py-[10px] px-[20px] bg-danger-500 text-white transition-all hover:bg-danger-400 rounded-md shadow-sm"
        >
          <span class="flex items-center">
            <i class="material-symbols-outlined mr-2">delete</i>
            {{ directT('dashboard.crm.delete') }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, onMounted } from "vue";
import { useClickOutside } from "@/utils/useClickOutside";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import { useRouter } from 'vue-router';
import { useI18n } from "vue-i18n";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// ייבוא מספר תמונות משתמשים קיימות במקום תמונת ברירת מחדל אחת
import user1 from "@/assets/images/users/user1.jpg";
import user2 from "@/assets/images/users/user2.jpg";
import user3 from "@/assets/images/users/user3.jpg";
import user4 from "@/assets/images/users/user4.jpg";
import user5 from "@/assets/images/users/user5.jpg";

// Create type for translation object structure
type TranslationObject = Record<string, any>;

interface Status {
  id: string;
  name: string;
  color: string;
}

interface Lead {
  id: string;
  name: string;
  email: string;
  source: string;
  status: string;
  created_at: string;
  phone: string;
  assigned_to?: string;
  assigned_user?: {
    email: string;
    display_name?: string;
    profile_picture_link?: string;
  };
  profileImageLink?: string;
}

export default defineComponent({
  name: "RecentLeads",
  setup() {
    const { isActive, toggleActive } = useClickOutside("dropdownToggle");
    const router = useRouter();
    const { locale } = useI18n();
    const statuses = ref<Status[]>([]);
    const organizationId = ref<string | null>(null);
    const loading = ref(false);
    const leads = ref<Lead[]>([]);
    const error = ref<string | null>(null);
    const showDeleteModal = ref(false);
    const leadToDelete = ref<Lead | null>(null);
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };
    
    // הגדרת סוגי פילטרי זמן זמינים
    const timeFilters = [
      directT('dashboard.thisDay'),
      directT('dashboard.thisWeek'),
      directT('dashboard.thisMonth'),
      directT('dashboard.thisYear')
    ];
    const selectedTimeFilter = ref(directT('dashboard.thisMonth'));

    // מערך של תמונות ברירת מחדל לשימוש כאווטארים
    const defaultAvatars = [user1, user2, user3, user4, user5];

    const itemsPerPage = 5;
    const currentPage = ref(1);

    // Get paginated items
    const paginatedItems = computed(() => {
      const startIndex = (currentPage.value - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return leads.value.slice(startIndex, endIndex);
    });

    // Calculate total pages
    const totalPages = computed(() => {
      return Math.ceil(leads.value.length / itemsPerPage);
    });

    // Calculate start and end items for pagination display
    const startItem = computed(() => {
      return (currentPage.value - 1) * itemsPerPage;
    });

    const endItem = computed(() => {
      const end = startItem.value + itemsPerPage;
      return end > leads.value.length ? leads.value.length : end;
    });

    // פונקציה להחלת פילטר זמן
    const applyTimeFilter = (filter: string) => {
      selectedTimeFilter.value = filter;
      toggleActive();
      currentPage.value = 1; // חזרה לעמוד הראשון
      fetchLeads(); // טעינה מחדש של הלידים עם הפילטר החדש
    };

    // חישוב מגבלת התאריכים לפי הפילטר הנוכחי
    const getDateFilter = () => {
      const now = new Date();
      let startDate;
      
      switch (selectedTimeFilter.value) {
        case "This Day":
          // היום הנוכחי (מתחילת היום)
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
          
        case "This Week": {
          // מתחילת השבוע הנוכחי (יום ראשון)
          const dayOfWeek = now.getDay(); // 0-6 (0 = יום ראשון)
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
          break;
        }
          
        case "This Month":
          // מתחילת החודש הנוכחי
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
          
        case "This Year":
          // מתחילת השנה הנוכחית
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
          
        default:
          // ברירת מחדל - יום אחד
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
      }
      
      // הפיכה לפורמט ISO לשימוש בשאילתה
      return startDate.toISOString();
    };

    // Navigation methods
    const goToPage = (page: number) => {
      currentPage.value = page;
    };

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--;
      }
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++;
      }
    };

    // Fetch leads and status options when component mounts
    onMounted(async () => {
      await fetchStatusOptions();
      await fetchLeads();
    });

    // Fetch status options from database
    const fetchStatusOptions = async () => {
      try {
        organizationId.value = await getCurrentOrgId();
        
        if (!organizationId.value) {
          console.warn('Cannot fetch statuses: Missing organization ID');
          return;
        }
        
        // Fetch statuses
        const { data: statusData, error: statusError } = await supabase
          .from('statusleads')
          .select('*')
          .eq('organization_id', organizationId.value);
        
        if (statusError) throw statusError;
        
        statuses.value = (statusData || []).map((item: any) => ({
          id: item.id,
          name: item.status,
          color: item.color || '#605DFF'
        }));
      } catch (error) {
        console.error('Error fetching status options:', error);
      }
    };

    // Fetch leads from database based on current organization
    const fetchLeads = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        if (!organizationId.value) {
          organizationId.value = await getCurrentOrgId();
        }
        
        if (!organizationId.value) {
          throw new Error('Unable to determine organization ID');
        }
        
        // חישוב תאריך ההתחלה לפילטור
        const startDate = getDateFilter();
        
        // שאילתה עם פילטר תאריך
        const { data, error: leadsError } = await supabase
          .from('leads')
          .select(`
            id, 
            name, 
            email, 
            phone, 
            status, 
            source, 
            created_at,
            assigned_to,
            profileImageLink
          `)
          .eq('org_id', organizationId.value)
          .gte('created_at', startDate) // פילטור לפי תאריך
          .order('created_at', { ascending: false })
          .limit(20);
        
        if (leadsError) throw leadsError;
        
        if (data) {
          leads.value = data.map((lead: any) => ({
            id: lead.id,
            name: lead.name || 'Unknown',
            email: lead.email || '',
            phone: lead.phone || '',
            source: lead.source || 'Unknown',
            status: lead.status || 'New',
            created_at: lead.created_at,
            assigned_to: lead.assigned_to,
            profileImageLink: lead.profileImageLink
          }));
        }
      } catch (err: any) {
        error.value = err.message || 'Failed to fetch leads';
        console.error('Error fetching leads:', err);
      } finally {
        loading.value = false;
      }
    };

    // Get status class for styling
    const getStatusClass = (status: string) => {
      if (!status) {
        // Default styling for no status
        return {
          backgroundColor: 'rgba(16, 185, 129, 0.15)', // Light green background
          color: '#10B981', // Green text
        };
      }
      
      // First, check if we have this status in our statuses array
      const statusObj = statuses.value.find(s => s.name.toLowerCase() === status.toLowerCase());
      
      if (statusObj && statusObj.color) {
        // Use the status's color from the database
        const colorHex = statusObj.color;
        
        // Convert hex to RGB for generating lighter background colors
        const r = parseInt(colorHex.slice(1, 3), 16);
        const g = parseInt(colorHex.slice(3, 5), 16);
        const b = parseInt(colorHex.slice(5, 7), 16);
        
        // Create a light background color with 15% opacity for light mode
        const bgColor = `rgba(${r}, ${g}, ${b}, 0.15)`;
        
        // Return inline styles for both background and text color
        return {
          backgroundColor: bgColor,
          color: colorHex
        };
      }
      
      // Fallback to predefined colors based on status name
      switch (status.toLowerCase()) {
        case 'new':
        case 'confirmed':
          return {
            backgroundColor: 'rgba(96, 93, 255, 0.15)',
            color: '#605DFF'
          };
        case 'in progress':
        case 'qualified': 
        case 'close':
          return {
            backgroundColor: 'rgba(16, 185, 129, 0.15)',
            color: '#10B981'
          };
        case 'pending':
          return {
            backgroundColor: 'rgba(245, 158, 11, 0.15)',
            color: '#F59E0B'
          };
        case 'rejected':
        case 'closed lost':
          return {
            backgroundColor: 'rgba(239, 68, 68, 0.15)',
            color: '#EF4444'
          };
        case 'closed won':
          return {
            backgroundColor: 'rgba(14, 165, 233, 0.15)',
            color: '#0EA5E9'
          };
        default:
          return {
            backgroundColor: 'rgba(156, 163, 175, 0.15)',
            color: '#9CA3AF'
          };
      }
    };

    // Function to get avatar for a lead
    const getLeadAvatar = (lead: any) => {
      // בדיקה האם יש תמונת פרופיל בשדה profileImageLink
      if (lead.profileImageLink) {
        return lead.profileImageLink;
      }
      
      // אם אין, נבחר תמונה אקראית מהתמונות שייבאנו
      // נשתמש במזהה הליד כדי לבחור תמונה קבועה עבור כל ליד
      const avatarIndex = lead.id ? lead.id.charCodeAt(0) % defaultAvatars.length : 0;
      return defaultAvatars[avatarIndex];
    };

    // Actions for leads
    const viewLead = (lead: Lead) => {
      router.push({ 
        name: 'LeadDetailPage', 
        params: { id: lead.id },
        query: { returnTo: router.currentRoute.value.fullPath }
      });
    };

    const editLead = (lead: Lead) => {
      router.push({ 
        name: 'LeadDetailPage', 
        params: { id: lead.id },
        query: { returnTo: router.currentRoute.value.fullPath, editMode: 'true' }
      });
    };

    // Close delete modal
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      leadToDelete.value = null;
    };

    // Confirm delete
    const confirmDelete = async () => {
      if (!leadToDelete.value) return;
      
      try {
        const { error: deleteError } = await supabase
          .from('leads')
          .delete()
          .eq('id', leadToDelete.value.id);
        
        if (deleteError) throw deleteError;
        
        // Remove from local list
        leads.value = leads.value.filter(lead => lead.id !== leadToDelete.value?.id);
        
      } catch (err: any) {
        console.error('Error deleting lead:', err);
        alert(`Failed to delete lead: ${err.message}`);
      } finally {
        closeDeleteModal();
      }
    };
    
    // Delete lead
    const deleteLead = (lead: Lead) => {
      leadToDelete.value = lead;
      showDeleteModal.value = true;
    };

    return {
      isActive,
      toggleActive,
      getStatusClass,
      loading,
      error,
      leads,
      paginatedItems,
      currentPage,
      totalPages,
      startItem,
      endItem,
      goToPage,
      prevPage,
      nextPage,
      getLeadAvatar,
      // Action functions
      viewLead,
      editLead,
      deleteLead,
      // Delete modal
      showDeleteModal,
      leadToDelete,
      closeDeleteModal,
      confirmDelete,
      // חשיפת הפילטרים
      timeFilters,
      selectedTimeFilter,
      applyTimeFilter,
      directT
    };
  },
});
</script>

<style lang="scss" scoped>
ol {
  li {
    &.active {
      a {
        background-color: #605dff;
        color: #fff;
        border-color: #605dff;
      }
    }
    
    &.disabled a {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }
}

.custom-tooltip {
  position: relative;
  
  &:hover:after {
    content: attr(data-text);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 12px;
    padding: 4px 8px;
    border-radius: 4px;
    white-space: nowrap;
    margin-bottom: 5px;
    z-index: 10;
  }
}
</style>
